/**
 * Logging Utility for IAD Excel Add-in
 * Provides structured logging with timestamps and context
 */

import { TELEMETRY_ENABLED, TELEMETRY_ENDPOINT } from './config';
// Second, independent destination. TELEMETRY_ENABLED drives /telemetry ->
// Vector -> Loki; API_LOGGING_ENABLED drives the addin-logs API. Routing every
// existing logger.info/warn/error through here too means the API stream is not
// limited to explicit user actions - it also gets the errors those actions
// produce, which is what makes a failure traceable end to end.
import { registerTelemetryFlush, shipToApi } from './actionLog';
import { isWafBlocked, WAF_BACKOFF_MS } from './waf';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogStream = 'client request' | 'request';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  data?: any;
  error?: Error;
  stream?: LogStream;
}

/**
 * Ships sanitized log entries to the same-origin /telemetry endpoint
 * (Caddy → Vector → Loki). Module-level singleton shared by every Logger.
 *
 * Hardened so logging can never crash the add-in: it is fully guarded for
 * runtimes without a DOM (e.g. the custom-functions runtime), batches sends,
 * redacts secret-looking content, caps payload size, and swallows all errors.
 */
const TELEMETRY_SHIP_LEVELS: ReadonlyArray<LogLevel> = ['info', 'warn', 'error'];

class TelemetryShipper {
  private buffer: Array<Record<string, unknown>> = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly maxBuffer = 50;
  private readonly flushIntervalMs = 10000;
  private readonly enabled: boolean;
  /**
   * Set when AWS WAF intercepted a /telemetry POST. This is the legacy duplicate
   * path (addin-logs holds the durable copy), so while blocked batches are
   * DROPPED rather than buffered - and nothing is sent, instead of a POST every
   * 10s feeding the rate-based rule that blocked us.
   */
  private wafBlockedUntil = 0;

  constructor() {
    this.enabled =
      TELEMETRY_ENABLED === 1 &&
      typeof fetch === 'function' &&
      typeof window !== 'undefined';

    if (this.enabled) {
      try {
        window.addEventListener('visibilitychange', () => {
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            this.flush(true);
          }
        });
        window.addEventListener('pagehide', () => this.flush(true));
      } catch {
        // No DOM event support; periodic + size-based flush still apply.
      }
    }
  }

  enqueue(entry: LogEntry): void {
    if (!this.enabled || !TELEMETRY_SHIP_LEVELS.includes(entry.level)) return;
    this.buffer.push(this.shape(entry));

    if (entry.level === 'error' || this.buffer.length >= this.maxBuffer) {
      this.flush();
    } else if (this.timer === null) {
      this.timer = setTimeout(() => this.flush(), this.flushIntervalMs);
    }
  }

  private shape(entry: LogEntry): Record<string, unknown> {
    let data: string | undefined;
    if (entry.data !== undefined) {
      try {
        data = JSON.stringify(entry.data);
      } catch {
        data = String(entry.data);
      }
      if (data && data.length > 1000) data = `${data.slice(0, 1000)}…`;
      if (data) {
        data = data.replace(/(token|password|authorization|bearer)["':=\s]+[^"',}\s]+/gi, '$1=***');
      }
    }
    return {
      timestamp: entry.timestamp,
      level: entry.level,
      context: entry.context,
      message: entry.message,
      data,
      error: entry.error ? entry.error.message : undefined,
    };
  }

  /** Send whatever is buffered now (the console `__insyncLog.flush()`). Returns the count. */
  flushNow(): number {
    const n = this.buffer.length;
    this.flush();
    return n;
  }

  private flush(useBeacon = false): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.enabled || this.buffer.length === 0) return;

    const batch = this.buffer;
    this.buffer = [];
    if (Date.now() < this.wafBlockedUntil) return; // WAF backoff - drop, see field note
    // NDJSON: one entry per line (matches the Vector http_server framing).
    const body = batch.map((e) => JSON.stringify(e)).join('\n');

    try {
      if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(TELEMETRY_ENDPOINT, new Blob([body], { type: 'application/x-ndjson' }));
        return;
      }
      void fetch(TELEMETRY_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-ndjson' },
        body,
        keepalive: true,
      })
        .then((res) => {
          if (isWafBlocked(res)) this.wafBlockedUntil = Date.now() + WAF_BACKOFF_MS;
        })
        .catch(() => {
          /* never throw from logging */
        });
    } catch {
      /* never throw from logging */
    }
  }
}

const telemetryShipper = new TelemetryShipper();
// actionLog owns the console hook but cannot import this module (logger already
// imports actionLog), so hand it the /telemetry flush instead.
registerTelemetryFlush(() => telemetryShipper.flushNow());

/**
 * Query params whose VALUES must never reach the log store.
 *
 * `username_filter` is the per-user access token that authorises data for that
 * user — it appears in most consensus_v2 URLs (endpoints.ts, IAD.ts) and anyone
 * who could read the logs could replay it. `filter` is matched loosely on
 * purpose: over-redacting a harmless param is free, under-redacting a new secret
 * one is not.
 */
const SECRET_PARAM = /(token|password|secret|filter|auth|key|credential|session)/i;

/** The URL with secret param values replaced, safe to put in `message`. */
function safeUrl(url: string): string {
  try {
    const [path, query] = String(url ?? '').split('?');
    if (!query) return path;
    const kept = query
      .split('&')
      .map((pair) => {
        const [k] = pair.split('=');
        return SECRET_PARAM.test(k) ? `${k}=***` : pair;
      })
      .join('&');
    return `${path}?${kept}`;
  } catch {
    return '';
  }
}

/**
 * Promote the parts of a URL worth querying into real fields. Grouping by
 * endpoint, or filtering every row for one ticker, should not require the
 * analyst to parse a URL string in SQL.
 */
function urlFacets(url: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  try {
    const [path, query] = String(url ?? '').split('?');
    out.endpoint = path;
    if (!query) return out;
    for (const pair of query.split('&')) {
      const [k, v] = pair.split('=');
      if (!k || v === undefined || SECRET_PARAM.test(k)) continue;
      const key = k.toLowerCase();
      if (key === 'ticker' || key === 'tickername' || key === 'tickers') {
        out.ticker = decodeURIComponent(v);
      } else if (key === 'fiscal_period' || key === 'period') {
        out.period = decodeURIComponent(v);
      } else if (key === 'broker') {
        out.broker = decodeURIComponent(v);
      } else if (key === 'page') {
        out.page = decodeURIComponent(v);
      }
    }
  } catch {
    /* best effort */
  }
  return out;
}

/** Size of a payload without logging its contents. */
function sizeOf(v: unknown): number | undefined {
  try {
    if (v === undefined || v === null) return undefined;
    return typeof v === 'string' ? v.length : JSON.stringify(v).length;
  } catch {
    return undefined;
  }
}

/** Row count when the payload looks tabular — the useful shape signal. */
export function rowsOf(v: any): number | undefined {
  try {
    if (Array.isArray(v)) return v.length;
    if (v && Array.isArray(v.data)) return v.data.length;
    if (v && Array.isArray(v.results)) return v.results.length;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Simple Logger class for Excel Add-in
 * Logs to console and can be extended to send to remote logging service
 */
class Logger {
  private context: string;
  private logs: LogEntry[] = [];
  private maxLogs: number = 100;

  constructor(context: string) {
    this.context = context;
  }

  /**
   * Create a child logger with a sub-context
   */
  child(subContext: string): Logger {
    return new Logger(`${this.context}.${subContext}`);
  }

  /**
   * Log a debug message
   */
  debug(message: string, data?: any): void {
    this.log('debug', message, data);
  }

  /**
   * Log an info message
   */
  info(message: string, data?: any): void {
    this.log('info', message, data);
  }

  /**
   * Log a warning message
   */
  warn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error, data?: any): void {
    this.log('error', message, data, error);
  }

  /**
   * Log function entry with parameters
   */
  enter(functionName: string, params?: Record<string, any>): void {
    const sanitizedParams = params 
      ? Object.fromEntries(
          Object.entries(params).map(([k, v]) => {
            // Mask sensitive fields
            if (k.toLowerCase().includes('password') || k.toLowerCase().includes('token')) {
              return [k, '***MASKED***'];
            }
            return [k, v];
          })
        )
      : undefined;
    
    this.log('debug', `→ ENTER: ${functionName}`, sanitizedParams);
  }

  /**
   * Log function exit with result
   */
  exit(functionName: string, result?: any): void {
    this.log('debug', `← EXIT: ${functionName}`, result);
  }

  /**
   * Log API request
   */
  apiRequest(method: string, url: string, body?: any): void {
    // The raw body is NOT logged: on the login path it is credentials, and on a
    // batch call it is thousands of keys. Its size is the part worth keeping.
    this.log('info', `API REQUEST: ${method} ${safeUrl(url)}`, {
      ...urlFacets(url),
      method,
      url: safeUrl(url),
      req_bytes: sizeOf(body),
    }, undefined, 'request');
  }

  /**
   * Log API response
   */
  apiResponse(
    method: string,
    url: string,
    status: number,
    _meta?: any,
    ms?: number,
    payload?: { bytes?: number; rows?: number }
  ): void {
    // Response CONTENT is not logged - it is customer data and can be megabytes.
    // Size and row count give you "did it return anything, and how much" without
    // putting the payload itself in a log store. They come from `payload`, which
    // the caller measures on the real response: `_meta` is a small descriptor
    // object, and sizing THAT is what made every res_bytes read ~99.
    this.log('info', `API RESPONSE: ${method} ${safeUrl(url)} → ${status}`, {
      ...urlFacets(url),
      method,
      url: safeUrl(url),
      status,
      ms,
      res_bytes: payload?.bytes,
      rows: payload?.rows,
    }, undefined, 'request');
  }

  /**
   * Log API error
   */
  apiError(method: string, url: string, error: Error, status?: number, ms?: number): void {
    this.log(
      'error',
      `API ERROR: ${method} ${safeUrl(url)} → ${status || 'unknown'}`,
      { ...urlFacets(url), method, url: safeUrl(url), status, ms },
      error,
      'request'
    );
  }

  /**
   * Get all stored logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear all stored logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as formatted string for debugging
   */
  exportLogs(): string {
    return this.logs.map(log => this.formatLogEntry(log)).join('\n');
  }

  private log(
    level: LogLevel,
    message: string,
    data?: any,
    error?: Error,
    stream?: LogStream
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context: this.context,
      message,
      data,
      error,
      stream,
    };

    // Store in memory (limited)
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Ship sanitized entry to the remote telemetry endpoint (no-op unless
    // telemetry is enabled and a DOM is available). Never throws.
    telemetryShipper.enqueue(entry);
    shipToApi(entry);

    // Log to console with formatting
    const formatted = this.formatLogEntry(entry);

    switch (level) {
      case 'debug':
        console.debug(`[${this.context}]`, message, data || '', error || '');
        break;
      case 'info':
        console.info(`[${this.context}]`, message, data || '', error || '');
        break;
      case 'warn':
        console.warn(`[${this.context}]`, message, data || '', error || '');
        break;
      case 'error':
        console.error(`[${this.context}]`, message, data || '', error || '');
        break;
    }
  }

  private formatLogEntry(entry: LogEntry): string {
    const timestamp = entry.timestamp;
    const level = entry.level.toUpperCase().padStart(5);
    const dataStr = entry.data ? ` | DATA: ${JSON.stringify(entry.data).substring(0, 200)}` : '';
    const errorStr = entry.error ? ` | ERROR: ${entry.error.message}` : '';
    return `${timestamp} [${level}] [${entry.context}] ${entry.message}${dataStr}${errorStr}`;
  }
}

/**
 * Create a logger instance for a specific context
 */
export function createLogger(context: string): Logger {
  return new Logger(context);
}

/**
 * Global logger instance
 */
export const logger = createLogger('IAD');

export default logger;
